import { TypedDocumentNode, gql } from '@apollo/client';
import { Fragment, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelXS } from '@sorare/core/src/atoms/typography';
import { rivals } from '@sorare/core/src/lib/glossary';

import { GridBackground } from '@sorare/gameplay/src/components/rivals/GridBackground';
import { BackgroundAligned } from '@sorare/gameplay/src/components/rivals/GridBackground/BackgroundAligned';

import { BoxOddsDialog } from 'components/rivals/seasons/BoxOddsDialog';
import { PodiumAvatar } from 'components/rivals/seasons/SeasonLeaderboard/SeasonLeaderboardHeader/PodiumAvatar';

import {
  SeasonLeaderboardPodium_footballRivalsDivisionLeaderboard,
  SeasonLeaderboardPodium_footballRivalsDivisionLeaderboardConfig,
} from './__generated__/index.graphql';

const BoxPreview = styled.img`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  object-fit: contain;
`;
const RewardLabel = styled(LabelXS)`
  text-transform: uppercase;
`;

type Props = {
  configs: SeasonLeaderboardPodium_footballRivalsDivisionLeaderboardConfig[];
  leaderboard: SeasonLeaderboardPodium_footballRivalsDivisionLeaderboard;
};
export const SeasonLeaderboardPodium = ({ leaderboard, configs }: Props) => {
  const { rankings, leaderboardConfig } = leaderboard;
  const leaderboardDivision = leaderboardConfig?.divisionConfig;
  const [selectedDivision, setSelectedDivision] = useState(leaderboardDivision);
  const [oddsDialogOpen, setOddsDialogOpen] = useState(false);
  const [selectedRank, setSelectedRank] = useState(1);

  if (!rankings) {
    return null;
  }

  const podium = [
    {
      rank: 2,
      ranking: rankings[1],
      horizontalDelta: -1.5,
      verticalDelta: 1,
      avatarWidth: 80,
    },
    {
      rank: 1,
      ranking: rankings[0],
      horizontalDelta: 0,
      verticalDelta: 0.5,
      avatarWidth: 100,
    },
    {
      rank: 3,
      ranking: rankings[2],
      horizontalDelta: 1.5,
      verticalDelta: 1,
      avatarWidth: 80,
    },
  ].filter(({ ranking }) => !!ranking);
  return (
    <>
      <GridBackground height={296} color="#454545">
        {podium.map(
          ({ rank, ranking, horizontalDelta, verticalDelta, avatarWidth }) => {
            const probabilisticBundleConfig =
              ranking.eligibleReward?.probabilisticBundleConfig;
            return (
              <Fragment key={rank}>
                <BackgroundAligned
                  verticalDelta={verticalDelta}
                  horizontalDelta={horizontalDelta}
                  maxWidth={2}
                  originOnTop
                >
                  <PodiumAvatar
                    key={rank}
                    user={ranking.manager.user}
                    rank={rank}
                    width={avatarWidth}
                  />
                </BackgroundAligned>

                {probabilisticBundleConfig && (
                  <BackgroundAligned
                    verticalDelta={2.6}
                    horizontalDelta={horizontalDelta}
                    maxWidth={2}
                    originOnTop
                  >
                    <Vertical gap={0.5}>
                      <RewardLabel color="var(--c-neutral-600)" bold as="span">
                        <FormattedMessage {...rivals.rewardLabel} />
                      </RewardLabel>
                      <ButtonBase
                        onClick={() => {
                          setSelectedRank(rank);
                          setOddsDialogOpen(true);
                        }}
                      >
                        <BoxPreview
                          src={probabilisticBundleConfig?.pictureUrl}
                          alt={probabilisticBundleConfig?.name}
                        />
                      </ButtonBase>
                    </Vertical>
                  </BackgroundAligned>
                )}
              </Fragment>
            );
          }
        )}
      </GridBackground>
      {oddsDialogOpen && selectedDivision && (
        <BoxOddsDialog
          open
          leaderboardsConfig={configs}
          initiallySelectedDivision={selectedDivision}
          initiallySelectedRank={selectedRank}
          onClose={() => {
            setOddsDialogOpen(false);
            setSelectedRank(1);
            setSelectedDivision(leaderboardDivision);
          }}
        />
      )}
    </>
  );
};
SeasonLeaderboardPodium.fragments = {
  footballRivalsDivisionLeaderboard: gql`
    fragment SeasonLeaderboardPodium_footballRivalsDivisionLeaderboard on FootballRivalsDivisionLeaderboard {
      id
      endDate
      leaderboardConfig {
        promotedCount
        relegatedCount
        divisionConfig {
          id
        }
        ...BoxOddsDialog_footballRivalsDivisionLeaderboardConfig
      }
      rankings {
        id
        manager {
          id
          user {
            slug
            ...PodiumAvatar_user
          }
        }
        eligibleReward {
          probabilisticBundleConfig {
            id
            name
            pictureUrl
          }
        }
      }
    }
    ${PodiumAvatar.fragments.user}
  ` as TypedDocumentNode<SeasonLeaderboardPodium_footballRivalsDivisionLeaderboard>,
  footballRivalsDivisionLeaderboardConfig: gql`
    fragment SeasonLeaderboardPodium_footballRivalsDivisionLeaderboardConfig on FootballRivalsDivisionLeaderboardConfig {
      ...BoxOddsDialog_footballRivalsDivisionLeaderboardConfig
    }
    ${BoxOddsDialog.fragments.footballRivalsDivisionLeaderboardConfig}
  ` as TypedDocumentNode<SeasonLeaderboardPodium_footballRivalsDivisionLeaderboardConfig>,
};
